import { runInAction } from 'mobx';
import React, { Reducer, ReducerState, useReducer } from 'react';

import { Paginated } from '../../../../adl-gen/common';
import { DbKey } from '../../../../adl-gen/common/db';
import { withI18n, WithI18nProps } from '../../../../internationalization/i18n-hoc';
// // TODO: Slow to load. These icons use a different set of parameters and are not
// // TODO: using IconProps, so the style 'xsmall' is ignored, for example.
// // tslint:disable-next-line: no-submodule-imports
// import { BiTrash } from 'react-icons/bi';
import { TiArrowSortedDown, TiArrowSortedUp, TiArrowUnsorted } from 'react-icons/ti';
import {
  // FIXME: This is broken cf BiTrash above
  BinSVGIcon as BinIcon,
  CloseIcon,
  IconProps,
  PlusIcon,
  TickCircleIcon
} from '../../../../ui/common/icons';
import { Button } from '../../../../ui/widgets/button/button';
import { SelectOption } from '../../../../ui/widgets/select/select';
import { Table, TableCell, TableRow } from '../../../../ui/widgets/table/table';
import { FormValue } from '../../../../ui/widgets/text-input/text-field';
import { SearchBox } from '../../components/search-box/search-box';
import { ListEntry, ListFieldInfo, OtherActionButton, SortColumn } from './list-types';
import { ListViewCell } from './list-view-cell';
import { ListLocalStore, createListLocalStore, filteredBySearchBoxes } from './list-local-store';

const styles = require('../common.css');

/// T = base type
/// R = (optional) reference type
export interface ListViewProps<T, R = void> extends WithI18nProps {
  list: Paginated<ListEntry<T>>;
  fields: ListFieldInfo<T, R>[];
  refData?: R;

  onSelect?(id: DbKey<T>): Promise<void>;
  onChange?(fieldName: string, item: ListEntry<T>): (value: string | number) => void;
  onCreate?(): void;
  onToggle?(fieldName: string, item: ListEntry<T>, value?: boolean): void;
  onDelete?(value: ListEntry<T>): void;
  /// Other hoisted action, not row-focused (eg. bulk upload)
  otherAction?: OtherActionButton;
}

type ViewStateAction<T> =
  | {
      kind: 'checkbox-change';
      payload: {
        fieldName: string;
        value: ListEntry<T>;
        oneOff?: boolean;
      };
    }
  | {
      kind: 'sort';
      payload: {
        sort?: SortColumn
      }
    }
  | {
      kind: 'delete';
      payload: { value: ListEntry<T> };
    }
  | {
      kind: 'search';
      payload: {
        fieldName: string;
        option?: SelectOption;
      };
    }
  | {
      kind: 'toggle-sort';
      payload: {
        index: number;
      };
    }
  | {
      kind: 'toggle';
      payload: {
        fieldName: string;
        itemId: DbKey<T>;
      };
    };

const viewStateReducer = <T, R>(viewStore: ListLocalStore<T, R>, action: ViewStateAction<T>) => {
  switch (action.kind) {
    case 'checkbox-change':
      return viewStore.onCheckboxChange({ ...action.payload });
    case 'sort':
      return viewStore.sortOnColumn(viewStore, action.payload.sort);
    case 'search':
      return viewStore.onSearch(action.payload.fieldName)(action.payload.option);
    case 'toggle-sort':
      return viewStore.toggleSortOnColumn(viewStore, action.payload.index);
    case 'toggle':
      return viewStore.onToggle(viewStore, action.payload.fieldName)(action.payload.itemId);
    case 'delete':
      return viewStore.onDelete(viewStore, action.payload.value);
    default:
      return viewStore;
  }
};

const ListView0 = <T, R>(props: ListViewProps<T, R>): JSX.Element => {

  const onChangeItem = (fieldName: string, item: ListEntry<T>) => (value: FormValue<string | number>) => {
      if (value.kind === 'valid') 
        runInAction(() => {
          if (props.onChange)
            props.onChange(fieldName, item)(value.value)
        });
  };

  const [viewState, dispatch] = useReducer<
    Reducer<ListLocalStore<T, R>, ViewStateAction<T>>,
    ReducerState<Reducer<any, any>>
  >(
    viewStateReducer,
    {
      data: props.list.items,
      t: props.t,
      refData: props.refData
    },
    createListLocalStore(props.fields, props.onSelect, onChangeItem)
  );

  const onChangeCheckboxItem = (fieldName: string, value: ListEntry<T>, oneOff: boolean = false) => {
    /// Update display, then post result
    const { onToggle } = props;
    const action: ViewStateAction<T> = { kind: 'checkbox-change', payload: { fieldName, value, oneOff } };
    // Update toggle values and set oneOff actioned (if appropriate)
    dispatch(action);

    runInAction(() => {
      if (onToggle) {
        const newState = viewStateReducer(viewState, action);
        const update = newState.toggleValues.find(tv => tv.fieldName === fieldName && tv.id === value.id);
        // FIXME: Upgrade Node to handle optional chaining
        // onToggle(fieldName, value, update?.value);
        onToggle(fieldName, value, update ? update.value : undefined);
      }
    });
  };

  const onDeleteItem = (value: ListEntry<T>) => {
    /// Update display, then post result
    const { onDelete } = props;

    dispatch({ kind: 'delete', payload: { value } });
    runInAction(() => {
      if (onDelete) {
        onDelete(value);
      }
    });
  };

  const onCreateItem = async (): Promise<void> => {
    const { onCreate } = props;
    runInAction(() => {
      if (onCreate) {
        onCreate();
      }
    })
  };

  const onOtherAction = async (): Promise<void> => {
    const { otherAction } = props;
    runInAction(() => {
      if (otherAction) {
        otherAction.action();
      }
    })
    return;
  };

  const onToggleItem = (fieldName: string) => async (itemId: DbKey<T>): Promise<void> => {
    /// Update display, then post result
    const { list, onToggle } = props;

    const action: ViewStateAction<T> = { kind: 'toggle', payload: { fieldName, itemId } };
    dispatch(action);

    runInAction(() => {
      const value = list.items.find(item => item.id === itemId);
      if (onToggle && value) {
        const newState = viewStateReducer(viewState, action);
        const update = newState.toggleValues.find(tv => tv.fieldName === fieldName && tv.id === itemId);
        onToggle(fieldName, value, update ? update.value : undefined);
      }
    });
  };

  const Controls = () => {
    const CreateButton = () => {
      return (
        <div className={styles.buttonList}>
          <Button onClick={onCreateItem} Icon={PlusIcon} size="small" variant="round">
            New
          </Button>
        </div>
      );
    };

    const OtherActionButton = () => {
      const { otherAction } = props;
      let icon: React.ComponentType<IconProps> | undefined;
      if (otherAction) {
        switch (otherAction.iconType) {
          case 'add':
            icon = PlusIcon;
            break;
          case 'cancel':
            icon = CloseIcon;
            break;
          case 'delete':
            icon = BinIcon;
            break;
          case 'confirm':
            icon = TickCircleIcon;
            break;
          default:
            icon = undefined;
        }
        const text = otherAction.text;
        return (
          <div className={styles.buttonList}>
            <Button onClick={onOtherAction} Icon={icon} size="small" variant="round">
              {text}
            </Button>
          </div>
        );
      } else {
        return null;
      }
    };

    const SearchBoxes = () => {
      return Array.from(viewState.searchBoxState.entries())
        .reverse()
        .map(([k, item], idx) => (
          <SearchBox
            title={item.title}
            options={item.options}
            value={item.value}
            onSearch={option => dispatch({ kind: 'search', payload: { fieldName: k, option } })}
            key={idx}
          />
        ));
    };

    return (
      <div className={styles.buttonListContainer}>
        {props.otherAction && OtherActionButton()}
        {props.onCreate && CreateButton()}
        {SearchBoxes()}
      </div>
    );
  };

  const SortIcon = (sort?: ListFieldInfo<T, R>['sortIconType']) => {
    switch (sort) {
      case 'up': return <TiArrowSortedUp size={12} />;
      case 'down': return <TiArrowSortedDown size={12} />;
      case 'unsorted': return <TiArrowUnsorted size={12} />;
      default: return null;
    }
  }

  const HeaderRow = () => {
    const { onDelete } = props;
    return (
      <TableRow header>
        {viewState.fields.map((field, index) => {
          if (field.kind === 'delete') {
            if (onDelete === undefined) {
              return null;
            }
          }
          if (field.displayed) {
            return (
              <TableCell key={index}>
                <div onClick={() => dispatch({ kind: 'toggle-sort', payload: { index } })} className={styles.uppercase}>
                  {field.label} {SortIcon(field.sortIconType)}
                </div>
              </TableCell>
            );
          } else {
            return null;
          }
        })}
      </TableRow>
    );
  };

  const Cell = (field: ListFieldInfo<T, R>, value: ListEntry<T>, index: number, refData?: R) => {
    const { t } = props;
    let toggleValue;
    let inputItemState;
    switch (field.kind) {
      case 'toggle': {
        toggleValue = viewState.toggleValues.find(tv => tv.fieldName === field.name && tv.id === value.id);
      }; break;
      case 'action': {
        inputItemState = viewState.fieldState.get(value.id);
      }; break;
    }
    return ListViewCell({
      field,
      value,
      index,
      onDelete: onDeleteItem,
      onToggle: onToggleItem,
      onOneOffAction: onChangeCheckboxItem,
      isActioned: () => viewState.actioned.find(entry => entry.value.id === value.id && entry.actioned) !== undefined,
      refData,
      toggleValue,
      inputItemState,
      t
    });
  };

  const Row = (item: ListEntry<T>, refData?: R) => {
    return (
      <React.Fragment>
        {viewState.fields.map((field, idx) => {
          if (field.displayed) {
            return Cell(field, item, idx, refData);
          } else {
            return undefined;
          }
        })}
      </React.Fragment>
    );
  };

  const alignments = viewState.getColumnAlignments;
  const visibility = viewState.getColumnVisibility('sm');

  return (
    <>
      <div>
        <Controls />
      </div>
      <div>
        <Table
          alignCenterColumn1={alignments[0] === 'centre'}
          alignCenterColumn2={alignments[1] === 'centre'}
          alignCenterColumn3={alignments[2] === 'centre'}
          alignCenterColumn4={alignments[3] === 'centre'}
          alignCenterColumn5={alignments[4] === 'centre'}
          alignCenterColumn6={alignments[5] === 'centre'}
          alignCenterColumn7={alignments[6] === 'centre'}
          alignCenterColumn8={alignments[7] === 'centre'}
          alignCenterColumn9={alignments[8] === 'centre'}
          alignCenterColumn10={alignments[9] === 'centre'}
          alignRightColumn1={alignments[0] === 'right'}
          alignRightColumn2={alignments[1] === 'right'}
          alignRightColumn3={alignments[2] === 'right'}
          alignRightColumn4={alignments[3] === 'right'}
          alignRightColumn5={alignments[4] === 'right'}
          alignRightColumn6={alignments[5] === 'right'}
          alignRightColumn7={alignments[6] === 'right'}
          alignRightColumn8={alignments[7] === 'right'}
          alignRightColumn9={alignments[8] === 'right'}
          alignRightColumn10={alignments[9] === 'right'}
          column1VisibleAfter={visibility[0]}
          column2VisibleAfter={visibility[1]}
          column3VisibleAfter={visibility[2]}
          column4VisibleAfter={visibility[3]}
          column5VisibleAfter={visibility[4]}
          column6VisibleAfter={visibility[5]}
          column7VisibleAfter={visibility[6]}
          column8VisibleAfter={visibility[7]}
          column9VisibleAfter={visibility[8]}
          column10VisibleAfter={visibility[9]}
          className={styles.noBorder}
        >
          {HeaderRow()}
          {filteredBySearchBoxes(viewState).map(item => {
            return <TableRow key={item.id}>{Row(item, (props.refData as unknown) as R)}</TableRow>;
          })}
        </Table>
      </div>
    </>
  );
};

export const ListView = withI18n(ListView0);



// WEBPACK FOOTER //
// ./src/admin/ui/common/list/list-view.tsx